/**
 * Every user-facing string for the engagement layer (streaks, check-ins, reviews, goals, celebrations).
 * Kept in one place so tone can be reviewed: warm, specific, never shaming, never guilt-tripping.
 * Rules: no "you failed/missed/broke"; lapses are framed as a fresh start; numbers are facts, not judgements.
 */
import { BRAND } from '@/brand';
const plural = (n: number, one: string, many = `${one}s`): string => (n === 1 ? one : many);

export const streakCopy = {
  label: (n: number): string => (n <= 0 ? 'Start a streak' : `${n}-day streak`),
  short: (n: number): string => String(Math.max(0, n)),
  aria: (n: number, freezes: number): string =>
    n <= 0
      ? 'No check-in streak yet. Check in today to start one.'
      : `${n}-day check-in streak. ${freezes} freeze ${plural(freezes, 'token')} left.`,
  tooltipTitle: (n: number): string => (n <= 0 ? 'Check in to start a streak' : `${n} ${plural(n, 'day')} in a row`),
  freezeExplainer: (freezes: number): string =>
    `Freeze tokens cover a day you can't check in, automatically — life happens. You have ${freezes} ${plural(freezes, 'token')}; you earn another every 7 days.`,
  longest: (n: number): string => `Longest so far: ${n} ${plural(n, 'day')}.`,
  welcomeBack: 'Welcome back. Your next check-in starts a fresh run.',
};

export const checkInCopy = {
  title: 'Daily check-in',
  subtitle: 'A minute to confirm what came in. Small, often, done.',
  intro: (n: number): string =>
    n === 0 ? "You're all caught up — nothing new to review." : `${n} new ${plural(n, 'transaction')} to look over. About a minute.`,
  cardHomeTitle: (n: number): string => (n === 0 ? 'All caught up' : `${n} to review`),
  cardHomeBody: (n: number): string =>
    n === 0 ? 'Check in anyway to keep your rhythm going.' : 'Confirm categories so your budget and reports stay accurate.',
  checkedInToday: 'Checked in today. See you tomorrow.',
  start: 'Start check-in',
  checkInNow: 'Check in',
  confirm: 'Looks right',
  change: 'Change category',
  split: 'Split',
  skip: 'Skip for now',
  suggested: 'Suggested',
  uncategorised: 'Uncategorised',
  finishEarly: 'Finish now',
  doneTitle: 'Your finances are up to date',
  doneSummary: (reviewed: number, changed: number, skipped: number): string => {
    if (reviewed === 0 && skipped === 0) return 'Nothing new today. Showing up still counts.';
    const parts = [`You reviewed ${reviewed} ${plural(reviewed, 'transaction')}`];
    if (changed > 0) parts.push(`recategorised ${changed}`);
    let s = parts.join(' and ') + '.';
    if (skipped > 0) s += ` ${skipped} saved for next time.`;
    return s;
  },
  doneStreak: (n: number): string => (n <= 1 ? 'Day one of your streak. Nice start.' : `That's ${n} days in a row.`),
  backHome: 'Back to Home',
};

export const weeklyCopy = {
  title: 'Weekly review',
  subtitle: 'Ten minutes to look back, notice, and set one intention.',
  promptTitle: 'Reflect',
  prompts: {
    wentWell: { label: 'What went well with money this week?', placeholder: 'Packed lunch four days, kept dining out under budget…' },
    surprised: { label: 'What surprised you?', placeholder: 'The car rego came earlier than I expected…' },
    intention: { label: 'One intention for next week', placeholder: 'When I get paid on Thursday, I move $150 to Holiday.' },
  },
  spent: 'Spent',
  income: 'Income',
  topCategories: 'Where it went',
  goalsMoved: 'Goals',
  wins: 'Wins this week',
  noWins: 'Quiet weeks count too.',
  vsLastWeek: 'vs last week',
  save: 'Save my review',
  doneTitle: 'Review saved',
  streak: (weeks: number): string => (weeks === 1 ? 'Your weekly review streak has started' : `${weeks}-week review streak`),
  doneBody: 'Your intention is saved with this week. You can read past reviews any time.',
  prompt: 'Your weekly review is ready',
  promptBody: 'Look back on the week and set one intention. About ten minutes.',
  unreviewed: (n: number): string => `${n} ${plural(n, 'transaction')} still to review — the numbers firm up once they're confirmed.`,
};

export const goalCopy = {
  onTrack: (goal: string, date: string): string => `On track to reach ${goal} by ${date}.`,
  behind: (amount: string, period: string): string => `${amount} per ${period} gets you there.`,
  achieved: (goal: string): string => `${goal} reached.`,
  nextMilestone: (pct: number, amount: string): string => `Next: ${pct}% at ${amount}`,
  milestoneToast: (pct: number, goal: string): string => (pct >= 100 ? `${goal}: goal reached` : `${goal} is ${pct}% of the way there`),
  milestoneBody: (pct: number): string =>
    pct >= 100 ? 'Every contribution got you here. Take a moment.' : pct >= 75 ? 'The final stretch — it speeds up from here.' : 'Steady progress. Keep going.',
  emptyTitle: 'Set your first goal',
  emptyBody: `An emergency fund, a holiday, a paid-off card — pick one and ${BRAND.name} shows the path.`,
};

export const insightCopy = {
  title: 'Insights',
  empty: `No insights right now. They appear as ${BRAND.name} learns your patterns.`,
  dismiss: 'Dismiss insight',
  dismissed: 'Insight dismissed',
  act: 'Take a look',
};

export const safeToSpendCopy = {
  title: 'Safe to spend',
  until: (date: string): string => `until ${date}`,
  explainer: "What's left after upcoming bills, your budget and goal contributions for this period.",
  breakdown: 'How we worked this out',
  negative: "Your bills, essentials and goal contributions need more than your spending accounts hold right now. Pausing a goal contribution or moving money from savings frees some up.",
  /** Tap target on the figure. */
  showWorking: 'Show how this was worked out',
};

export const netWorthCopy = {
  title: 'Net worth',
  explainer: 'Everything you own minus everything you owe.',
  ahaTitle: "Here's where you stand",
  ahaNetWorth: "This is your starting line — everything you own minus everything you owe. From here, every step shows.",
  ahaSafe: "And this is what you can spend in that time without touching bills or goals.",
  consolidated: 'All entities',
  thisEntity: 'This entity',
  delta: 'over 30 days',
};

export const calmCopy = {
  title: 'Calm mode',
  short: 'Calm mode is on — streaks, celebrations and nudges are hidden.',
  explainer:
    'Calm mode hides streaks, confetti, achievement pop-ups and nudges. Your data, budgets and any reminders you set stay exactly as they are. Turn it off any time.',
  honest:
    `${BRAND.name} uses a few habit techniques — streaks, progress rings, small celebrations — because they help most people check in regularly. If they don't help you, switch them off. Nothing else changes.`,
  change: 'Change in settings',
};

export const achievementCopy = {
  unlocked: (title: string): string => `Achievement: ${title}`,
  galleryTitle: 'Badges',
  galleryBody: 'These reflect real money skills, not just clicks. Greyed ones show how to unlock them.',
  empty: 'Achievements unlock as you build skills — a first reconciliation, three months of emergency savings, a paid-off card.',
  locked: 'Not yet',
};

export const onboardingCopy = {
  welcomeTitle: `Welcome to ${BRAND.name}`,
  welcomeBody: "Let's get you to a clear picture in about three minutes. You can skip any step.",
  entityQuestion: 'Whose money are we starting with?',
  countryTitle: 'Where are you based?',
  countryBody: 'This sets your currency, date format and financial year. You can change it later.',
  accountTitle: 'Add your first account',
  accountBody: 'Start with your everyday account. A balance is enough — you can import history later.',
  goalTitle: 'What are you working towards?',
  goalBody: `One goal is plenty to start. ${BRAND.name} will show the path and cheer the milestones.`,
  intentionLabel: 'Make a plan (optional)',
  intentionHint: 'People who decide when and how are far more likely to follow through.',
  intentionPlaceholder: 'When I get paid, I move $200 to this goal.',
  ritualsTitle: 'Build a gentle rhythm',
  ritualsBody: 'Small, regular check-ins beat big monthly clean-ups. Choose what suits you.',
  doneTitle: "You're set up",
  doneBody: 'Your money has a home. Here are good next steps when you have a moment.',
};
